import { Component, inject, Input } from '@angular/core';
import { Router } from '@angular/router';
import { GroupedAdditionalInfo, HtstInfoField, Sensor } from '../../models/additionalInfo.model';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { AssetDetail, NavButtonComponent, UiService, UnitConversionPipe } from '@lp-ui-lib';
import { I18nService } from '@lp-ui-lib';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { HtstInfoDataEMComponent } from './htst-info/htst-info';
import { logError } from '@lp-ui-lib';

@Component({
  selector: 'app-additional-info-em',
  templateUrl: './additional-info.html',
  styleUrls: ['./additional-info.scss'],
  imports: [
    UnitConversionPipe,
    NzModalModule,
    NzListModule,
    HtstInfoDataEMComponent,
    NzCollapseModule,
    NavButtonComponent,
    NzIconModule,
  ],
})
export class AdditionalInfoEMComponent {
  @Input() asset: AssetDetail | any = null;
  @Input() Sensor: Sensor | any = null;
  @Input() groupedInfoValues: GroupedAdditionalInfo[] | any = null;

  public overlayImage = null;
  public addInfo: boolean[] = [false, false, false, false];
  installationDate: any = null;
  inletHtstInfo: HtstInfoField = {};
  outletHtstInfo: HtstInfoField = {};
  showSensorData = true;

  get InstallationData(): any {
    function formatSerialNumber(input: string): string {
      const cached_input = '' + input;
      try {
        input = input.toUpperCase();
        const strArray = Array.from(Array(input.length).keys()).map((i: number) => input.charAt(i));
        return strArray.map((char: string) => char).reduce((a: string, b: string) => a + b);
      } catch (e) {
        logError(e);
        return cached_input;
      }
    }

    function formatMAC(input: string): any {
      const cached_input = '' + input;
      try {
        if (input.length !== 12) return input;
        input = input.toUpperCase();
        const strArray: any = Array.from(Array(input.length).keys()).map((i: number) =>
          input.charAt(i)
        );
        return strArray
          .map((char: string, index: number, array: []) =>
            index % 2 === 0 || index >= array.length - 1 ? char : char + ':'
          )
          .reduce((a: string, b: string) => a + b);
      } catch {
        return cached_input;
      }
    }
    if (
      !!this.Sensor &&
      this.asset?.equipmentType !== 'Rotating Equipment' &&
      this.asset?.equipmentType !== 'Steam Trap' && // show for leakage sensor only
      (!!this.Sensor.sensorDigitID || !!this.Sensor.edgeDigitID || !!this.Sensor.junctionBoxSlot)
    ) {
      const output = [];
      output.push({
        header: this.i18n.langString('sensor_name'),
        items: [
          {
            text: this.Sensor.sensorName,
            value: null,
            valueType: null,
            category: this.i18n.langString('sensor_name'),
            icon: null,
          },
        ],
      });
      if (this.Sensor.sensorDigitID)
        output.push({
          header: this.i18n.langString('sensor_number'),
          items: [
            {
              text: this.Sensor.sensorDigitID,
              value: null,
              valueType: null,
              category: this.i18n.langString('sensor_number'),
              icon: null,
            },
          ],
        });
      if (this.Sensor.edgeDigitID)
        output.push({
          header: this.i18n.langString('edge_device_number'),
          items: [
            {
              text: this.Sensor.edgeDigitID,
              value: null,
              valueType: null,
              category: this.i18n.langString('edge_device_number'),
              icon: null,
            },
          ],
        });
      if (this.Sensor.junctionBoxSlot)
        output.push({
          header: this.i18n.langString('junction_box_slot'),
          items: [
            {
              text: this.Sensor.markerText,
              value: null,
              valueType: null,
              category: this.i18n.langString('junction_box_slot'),
              icon: null,
            },
          ],
        });
      return output;
    } else if (
      !!this.Sensor &&
      this.asset?.equipmentType === 'Rotating Equipment' &&
      (!!this.Sensor.sensorDigitID || !!this.Sensor.sensorName)
    ) {
      const output = [];
      this.showSensorData = false;
      output.push({
        header: this.i18n.langString('sensor_name'),
        items: [
          {
            text: this.Sensor.sensorName,
            value: null,
            valueType: null,
            category: this.i18n.langString('sensor_name'),
            icon: null,
          },
        ],
      });
      if (this.Sensor.sensorDigitID)
        output.push({
          header: this.i18n.langString('sensor_number'),
          items: [
            {
              text: this.Sensor.sensorDigitID,
              value: null,
              valueType: null,
              category: this.i18n.langString('sensor_number'),
              icon: null,
            },
          ],
        });
      return output;
    } else if (
      !!this.Sensor &&
      this.asset?.equipmentType === 'Steam Trap' && // show for steamtrap sensor only
      (!!this.Sensor.inletNumber ||
        !!this.Sensor.outletNumber ||
        !!this.Sensor.DAU ||
        !!this.Sensor.gateway) &&
      (this.Sensor.gateway != null || this.Sensor.DAU != null)
    ) {
      const output = [];
      output.push({
        header: this.i18n.langString('sensor_name'),
        items: [
          {
            text: this.Sensor.sensorName,
            value: null,
            valueType: null,
            category: this.i18n.langString('sensor_name'),
            icon: null,
          },
        ],
      });
      if (this.Sensor.inletNumber)
        output.push({
          header: this.i18n.langString('inlet_number'),
          items: [
            {
              text: formatSerialNumber(this.Sensor.inletNumber),
              value: null,
              valueType: null,
              category: this.i18n.langString('inlet_number'),
              icon: null,
            },
          ],
        });
      if (this.Sensor.outletNumber)
        output.push({
          header: this.i18n.langString('outlet_number'),
          items: [
            {
              text: formatSerialNumber(this.Sensor.outletNumber),
              value: null,
              valueType: null,
              category: this.i18n.langString('outlet_number'),
              icon: null,
            },
          ],
        });
      if (this.Sensor.DAU)
        output.push({
          header: this.i18n.langString('dau_code'),
          items: [
            {
              text: formatMAC(this.Sensor.DAU),
              value: null,
              valueType: null,
              category: this.i18n.langString('dau_code'),
              icon: null,
            },
          ],
        });
      if (this.Sensor.gateway)
        output.push({
          header: this.i18n.langString('gateway_code'),
          items: [
            {
              text: formatSerialNumber(this.Sensor.gateway),
              value: null,
              valueType: null,
              category: this.i18n.langString('gateway_code'),
              icon: null,
            },
          ],
        });
      return output;
    }
    return null;
  }

  get GroupedAdditionalInfo(): {
    header: string;
    items: {
      text: string;
      value: number;
      valueType: number;
      category: string;
      icon: string;
      unitPrefix: string;
    }[];
  }[] {
    const groupBy = function (xs: any, key: any) {
      return xs.reduce(function (rv: any, x: any) {
        (rv[x[key]] = rv[x[key]] || []).push(x);
        return rv;
      }, {});
    };

    if (!this.asset) return [];
    const grouped = groupBy(this.asset.additionalInfo, 'category');
    return Object.keys(grouped).map((key) => {
      return {
        header: key,
        items: grouped[key],
      };
    });
  }

  get HtstInfo(): boolean {
    if (!!this.Sensor && this.Sensor.inletGateway != null) {
      this.installationDate = this.Sensor.sensorCreationDate;
      this.inletHtstInfo = {
        gatewayId: this.Sensor.inletGateway,
        macAddress: this.Sensor?.inletMac?.match(/.{1,2}/g)?.join(':'),
        dauModel: this.Sensor.inletMacModel,
        sensorColor: this.Sensor.inletColorCode,
      };
      this.outletHtstInfo = {
        gatewayId: this.Sensor.outletGateway,
        macAddress: this.Sensor?.outletMac?.match(/.{1,2}/g)?.join(':'),
        dauModel: this.Sensor.outletMacModel,
        sensorColor: this.Sensor.outletColorCode,
      };
      return true;
    }
    return false;
  }

  get isMobile(): boolean {
    return this.ui.IsMobile();
  }
  get isTablet(): boolean {
    return this.ui.IsTablet();
  }

  get isDesktop(): boolean {
    return this.ui.IsDesktop();
  }

  public i18n = inject(I18nService);
  private ui = inject(UiService);

  private router = inject(Router);

  openDocument(link: string) {
    window.open(link, '_blank', 'noreferrer');
    // noreferrer needed, otherwise the pdf reader in the new tab will freeze current tab for some reason
  }

  parseGroupHeader(header: string) {
    const parsed: any = this.i18n.parse(header);
    return parsed[this.i18n.measurementSystem()] ? parsed[this.i18n.measurementSystem()] : parsed;
  }
}
